import React, { useEffect, useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
  Button,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  CircularProgress,
  Box,
  FormControlLabel,
  Checkbox,
  TablePagination,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Divider,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { getToken, isValidAdminToken } from "../utils/Authe";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FolderIcon from '@mui/icons-material/Folder';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const allTypes = ["MCQ", "FIB", "TRUEFALSE", "SUB"];

const QuizCreationPage = () => {
  const theme = useTheme();
  const [selectedFolderFilter, setSelectedFolderFilter] = useState(null);
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [selectedStudentForResult, setSelectedStudentForResult] = useState(null);
  const [quizResults, setQuizResults] = useState(null);
  const [negMarking, setNegMarking] = useState(false);
  const [deleteQuizModalOpen, setDeleteQuizModalOpen] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [quizModalOpen, setQuizModalOpen] = useState(false);
  const [viewQuizzesModalOpen, setViewQuizzesModalOpen] = useState(false);
  const [folderQuizzes, setFolderQuizzes] = useState([]);
  const [quizTitle, setQuizTitle] = useState("");
  const [passingPercentage, setPassingPercentage] = useState(40.0);
  const [maxAttempts, setMaxAttempts] = useState(100);
  const [timeLimit, setTimeLimit] = useState("");
  const [questionTypes, setQuestionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  // Pagination and search states for folders
  const [folderPage, setFolderPage] = useState(0);
  const [folderRowsPerPage, setFolderRowsPerPage] = useState(5);
  const [folderSearch, setFolderSearch] = useState("");
  // Pagination and search states for students
  const [studentPage, setStudentPage] = useState(0);
  const [studentRowsPerPage, setStudentRowsPerPage] = useState(5);
  const [studentSearch, setStudentSearch] = useState("");
  // Pagination for quizzes in modal
  const [quizPage, setQuizPage] = useState(0);
  const [quizRowsPerPage, setQuizRowsPerPage] = useState(5);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  // New states for all quizzes
  const [allQuizzes, setAllQuizzes] = useState([]);
  const [loadingQuizzes, setLoadingQuizzes] = useState(true);
  const [quizSearch, setQuizSearch] = useState("");
  const [allQuizPage, setAllQuizPage] = useState(0);
  const [allQuizRowsPerPage, setAllQuizRowsPerPage] = useState(5);
  const [viewDetailsModalOpen, setViewDetailsModalOpen] = useState(false);
  const [selectedQuizDetails, setSelectedQuizDetails] = useState(null);
  const [infoModalOpen, setInfoModalOpen] = useState(false);

  const [studentsForResult, setStudentsForResult] = useState([]);

  const DetailItem = ({ label, value, icon, color = "#6b7280" }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: "14px",
          background: `linear-gradient(135deg, ${color}22 0%, ${color}11 100%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: `0 4px 12px ${color}30`,
        }}
      >
        <i className={icon} style={{ fontSize: "1.4rem", color }}></i>
      </Box>
      <Box>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );

  useEffect(() => {
    if (folders.length > 0) {
      fetchAllQuizzes();
    }
  }, [folders]);

  useEffect(() => {
    const token = getToken();
    if (!token || !isValidAdminToken(token)) {
      navigate("/");
    } else {
      Promise.all([fetchStudents(token), fetchFolders(token)]).finally(() => {
        setLoading(false);
      });
    }
  }, [navigate]);

  useEffect(() => {
    if (folders.length > 0) {
      const token = getToken();
      const fetchAllQuizzes = async () => {
        setLoadingQuizzes(true);
        const quizzes = [];
        for (const folder of folders) {
          try {
            const response = await fetch(
              `${API_BASE_URL}/client-admin/folder/${folder.id}/quizzes/`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
            if (response.ok) {
              const data = await response.json();
              const folderQuizzesWithInfo = data.quizzes.map((q) => ({
                ...q,
                folder_name: data.folder_name,
                folder_id: data.folder_id,
              }));
              quizzes.push(...folderQuizzesWithInfo);
            }
          } catch (error) {
            console.error("Error fetching quizzes for folder:", error);
          }
        }
        setAllQuizzes(quizzes);
        setLoadingQuizzes(false);
      };
      fetchAllQuizzes();
    }
  }, [folders]);

  const fetchStudents = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/students/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setStudents(data.students);
      } else {
        console.error("Failed to fetch students");
      }
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  };

  const fetchStudentsForResult = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/students/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setStudentsForResult(data.students);
      } else {
        console.error("Failed to fetch students for result");
      }
    } catch (error) {
      console.error("Error fetching students for result:", error);
    }
  };

  const fetchQuizResults = async (studentId, quizId) => {
    try {
      const token = getToken();
      const response = await fetch(
        `${API_BASE_URL}/students/${studentId}/quiz-results/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        console.log("Quiz results data:", data);
        console.log("Looking for quiz_id:", quizId);
        const selectedQuizResult = data.quiz_history.find(
          (quiz) => quiz.quiz_id === parseInt(quizId)
        );
        console.log("Found result:", selectedQuizResult);
        setQuizResults(selectedQuizResult || null);
      } else {
        console.error("Failed to fetch quiz results");
        setQuizResults(null);
      }
    } catch (error) {
      console.error("Error fetching quiz results:", error);
      setQuizResults(null);
    }
  };

  const fetchFolders = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/folders/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setFolders(data);
      } else {
        console.error("Failed to fetch folders");
      }
    } catch (error) {
      console.error("Error fetching folders:", error);
    }
  };

  const fetchFolderQuizzes = async (folderId, token) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/client-admin/folder/${folderId}/quizzes/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setFolderQuizzes(data.quizzes || []);
      } else {
        console.error("Failed to fetch quizzes for folder");
      }
    } catch (error) {
      console.error("Error fetching quizzes for folder:", error);
    }
  };

  // Filtered and paginated folders
  const filteredFolders = useMemo(() => {
    return folders.filter((folder) =>
      folder.name.toLowerCase().includes(folderSearch.toLowerCase())
    );
  }, [folders, folderSearch]);

  const paginatedFolders = useMemo(() => {
    const start = folderPage * folderRowsPerPage;
    return filteredFolders.slice(start, start + folderRowsPerPage);
  }, [filteredFolders, folderPage, folderRowsPerPage]);

  // Filtered and paginated students
  const filteredStudents = useMemo(() => {
    return students.filter((student) =>
      student.name.toLowerCase().includes(studentSearch.toLowerCase())
    );
  }, [students, studentSearch]);

  const paginatedStudents = useMemo(() => {
    const start = studentPage * studentRowsPerPage;
    return filteredStudents.slice(start, start + studentRowsPerPage);
  }, [filteredStudents, studentPage, studentRowsPerPage]);

  // Paginated quizzes for modal
  const paginatedQuizzes = useMemo(() => {
    const start = quizPage * quizRowsPerPage;
    return folderQuizzes.slice(start, start + quizRowsPerPage);
  }, [folderQuizzes, quizPage, quizRowsPerPage]);

  // Filtered and paginated all quizzes
  const filteredQuizzes = useMemo(() => {
    return allQuizzes
      .filter((quiz) =>
        quiz.title.toLowerCase().includes(quizSearch.toLowerCase())
      )
      .filter((quiz) =>
        selectedFolderFilter ? quiz.folder_id === selectedFolderFilter.id : true
      );
  }, [allQuizzes, quizSearch, selectedFolderFilter]);

  const paginatedAllQuizzes = useMemo(() => {
    const start = allQuizPage * allQuizRowsPerPage;
    return filteredQuizzes.slice(start, start + allQuizRowsPerPage);
  }, [filteredQuizzes, allQuizPage, allQuizRowsPerPage]);

  const handleExportQuiz = async (quizId) => {
    try {
      const token = getToken();
      const response = await fetch(
        `${API_BASE_URL}/quiz/admin/export-quiz/${quizId}/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Export failed: ${response.status} ${response.statusText}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Quiz_${quizId}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSnackbarMessage("Quiz exported successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Export failed:", error);
      setSnackbarMessage(`Export failed: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleDeleteQuiz = async () => {
    if (!selectedQuiz) return;
    try {
      const token = getToken();
      const response = await fetch(
        `${API_BASE_URL}/quiz/${selectedQuiz.id}/delete-quiz/`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        setDeleteQuizModalOpen(false);
        setSelectedQuiz(null);
        fetchAllQuizzes();
        setSnackbarOpen(true);
        setSnackbarMessage("Quiz deleted successfully!");
        setSnackbarSeverity("success");
      } else {
        console.error("Failed to delete quiz");
        setSnackbarOpen(true);
        setSnackbarMessage("Failed to delete quiz.");
        setSnackbarSeverity("error");
      }
    } catch (error) {
      console.error("Error deleting quiz:", error);
      setSnackbarOpen(true);
      setSnackbarMessage(`Error deleting quiz: ${error.message}`);
      setSnackbarSeverity("error");
    }
  };

  const handleExportQuizReview = async (studentId, quizId, attemptId) => {
    try {
      const token = getToken();
      const response = await fetch(
        `${API_BASE_URL}/students/${studentId}/export-quiz-review/${quizId}/${attemptId}/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Export failed: ${response.status} ${response.statusText}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Quiz_Review_${selectedStudentForResult?.name || "Student"}_${attemptId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSnackbarMessage("Quiz review exported successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Export failed:", error);
      setSnackbarMessage(`Export failed: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleExportFolderQuizzes = async () => {
    try {
      const token = getToken();
      if (!selectedFolder?.id) {
        setSnackbarMessage("No course selected.");
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
        return;
      }
      const response = await fetch(
        `${API_BASE_URL}/quiz/admin/export-folder-quizzes/${selectedFolder.id}/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Export failed: ${response.status} ${response.statusText}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Quizzes_Folder_${selectedFolder.name}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSnackbarMessage("All quizzes exported successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Export failed:", error);
      setSnackbarMessage(`Export failed: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleExportAllQuizzes = async () => {
    try {
      const token = getToken();
      const response = await fetch(
        `${API_BASE_URL}/quiz/admin/export-all-quizzes/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(
          `Export failed: ${response.status} ${response.statusText}`
        );
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `All_Quizzes_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSnackbarMessage("All quizzes exported successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Export failed:", error);
      setSnackbarMessage(`Export failed: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const fetchAllQuizzes = async () => {
    const token = getToken();
    setLoadingQuizzes(true);
    const quizzes = [];
    for (const folder of folders) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/client-admin/folder/${folder.id}/quizzes/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          const folderQuizzesWithInfo = data.quizzes.map((q) => ({
            ...q,
            folder_name: data.folder_name,
            folder_id: data.folder_id,
          }));
          quizzes.push(...folderQuizzesWithInfo);
        }
      } catch (error) {
        console.error("Error fetching quizzes for course:", error);
      }
    }
    setAllQuizzes(quizzes);
    setLoadingQuizzes(false);
  };

  const handleAssignFolder = (student) => {
    setSelectedStudent(student);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedFolder(null);
  };

  const handleAssign = async () => {
    if (!selectedFolder) return;
    try {
      const token = getToken();
      const response = await fetch(
        `${API_BASE_URL}/folders/assign_to_student/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            student_id: selectedStudent.id,
            folder_names: [selectedFolder.name],
          }),
        }
      );
      if (response.ok) {
        await fetchStudents(token);
        handleCloseModal();
      } else {
        console.error("Failed to assign course");
      }
    } catch (error) {
      console.error("Error assigning course:", error);
    }
  };

  const handleRemoveFolder = (student) => {
    setSelectedStudent(student);
    setRemoveModalOpen(true);
  };

  const handleCloseRemoveModal = () => {
    setRemoveModalOpen(false);
    setSelectedFolder(null);
  };

  const handleRemove = async () => {
    if (!selectedFolder) return;
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/students/remove_folder/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          student_id: selectedStudent.id,
          folder_name: selectedFolder.name,
        }),
      });
      if (response.ok) {
        await fetchStudents(token);
        handleCloseRemoveModal();
      } else {
        console.error("Failed to remove course");
      }
    } catch (error) {
      console.error("Error removing course:", error);
    }
  };

  const handleOpenQuizModal = (folder) => {
    setSelectedFolder(folder);
    setQuizModalOpen(true);
  };

  const handleCloseQuizModal = () => {
    setQuizModalOpen(false);
    setSelectedFolder(null);
    setQuizTitle("");
    setPassingPercentage(40.0);
    setMaxAttempts(100);
    setTimeLimit("");
    setQuestionTypes([]);
  };

  const handleOpenViewQuizzesModal = async (folder) => {
    const token = getToken();
    setSelectedFolder(folder);
    setQuizPage(0);
    await fetchFolderQuizzes(folder.id, token);
    setViewQuizzesModalOpen(true);
  };

  const handleCloseViewQuizzesModal = () => {
    setViewQuizzesModalOpen(false);
    setSelectedFolder(null);
    setFolderQuizzes([]);
    setQuizPage(0);
  };

  const handleOpenDetails = (quiz) => {
    setSelectedQuizDetails(quiz);
    setViewDetailsModalOpen(true);
  };

  const handleCloseDetails = () => {
    setViewDetailsModalOpen(false);
    setSelectedQuizDetails(null);
  };

  const handleAllTypesChange = (e) => {
    if (e.target.checked) {
      setQuestionTypes([...allTypes]);
    } else {
      setQuestionTypes([]);
    }
  };

  const handleTypeChange = (type) => (e) => {
    if (e.target.checked) {
      setQuestionTypes((prev) => [...prev, type]);
    } else {
      setQuestionTypes((prev) => prev.filter((t) => t !== type));
    }
  };

  const getTypeLabel = (type) => {
    if (type === "TRUEFALSE") return "TRUE FALSE";
    if (type === "SUB") return "SINGLE LINE";
    return type;
  };

  const handleCreateQuiz = async () => {
    if (!selectedFolder || !quizTitle || questionTypes.length === 0) return;
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/quiz/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          folder: selectedFolder.id,
          title: quizTitle,
          question_types: questionTypes,
          passing_percentage: passingPercentage,
          max_attempts: maxAttempts,
          time_limit_minutes: timeLimit ? parseInt(timeLimit) : null,
          neg_marking: negMarking,
        }),
      });
      if (response.ok) {
        setSnackbarOpen(true);
        setSnackbarMessage("Quiz created successfully!");
        setSnackbarSeverity("success");
        handleCloseQuizModal();
        fetchAllQuizzes();
      } else {
        console.error("Failed to create quiz");
      }
    } catch (error) {
      console.error("Error creating quiz:", error);
    }
  };

  // Pagination handlers
  const handleFolderPageChange = (event, newPage) => setFolderPage(newPage);
  const handleFolderRowsPerPageChange = (event) => {
    setFolderRowsPerPage(parseInt(event.target.value, 10));
    setFolderPage(0);
  };
  const handleStudentPageChange = (event, newPage) => setStudentPage(newPage);
  const handleStudentRowsPerPageChange = (event) => {
    setStudentRowsPerPage(parseInt(event.target.value, 10));
    setStudentPage(0);
  };
  const handleQuizPageChange = (event, newPage) => setQuizPage(newPage);
  const handleQuizRowsPerPageChange = (event) => {
    setQuizRowsPerPage(parseInt(event.target.value, 10));
    setQuizPage(0);
  };
  const handleAllQuizPageChange = (event, newPage) => setAllQuizPage(newPage);
  const handleAllQuizRowsPerPageChange = (event) => {
    setAllQuizRowsPerPage(parseInt(event.target.value, 10));
    setAllQuizPage(0);
  };

  // Search clear handlers
  const clearFolderSearch = () => {
    setFolderSearch("");
    setFolderPage(0);
  };
  const clearStudentSearch = () => {
    setStudentSearch("");
    setStudentPage(0);
  };
  const clearQuizSearch = () => {
    setQuizSearch("");
    setAllQuizPage(0);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: theme.palette.background.default

        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        // maxWidth: 1200,
        // mx: "auto",
        // minHeight: "100vh",
        background: theme.palette.background.default,
        // borderRadius: "24px",
        // boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <div>
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              fontWeight: "800",
              background: theme.palette.mode === 'light'
                ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Quiz Creation
          </Typography>

          <Typography
            variant="subtitle1"
            sx={{ color: theme.palette.text.secondary, fontWeight: "600", mt: 1 }}
          >
            Create quizzes from Courses.
          </Typography>
        </div>
        <Tooltip title="User Guide" arrow>
          <IconButton
            onClick={() => setInfoModalOpen(true)}
            sx={{
              bgcolor: "rgba(102, 126, 234, 0.1)",
              "&:hover": { bgcolor: "rgba(102, 126, 234, 0.2)" },
              p: 1.5,
            }}
          >
            <InfoOutlinedIcon sx={{ color: "#667eea" }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Folders Section */}
      <Paper
        sx={{
          p: 4,
          mb: 4,
          borderRadius: "16px",
          boxShadow: "0 8px 30px rgba(0, 0, 0, 0.1)",
          border: `1px solid ${theme.palette.divider}`,
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
          },
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Typography
          variant="h5"
          sx={{ color: theme.palette.text.primary, fontWeight: "700", mb: 2 }}
        >
          Create Quizzes from Courses
        </Typography>
        <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
        {/* Folder Search */}
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            placeholder="Search Courses..."
            value={folderSearch}
            onChange={(e) => {
              setFolderSearch(e.target.value);
              setFolderPage(0);
            }}
            variant="outlined"
            size="small"
            sx={{ width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: folderSearch && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={clearFolderSearch}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Chip
            label={`${filteredFolders.length} Course/Folder${filteredFolders.length !== 1 ? "s" : ""
              }`}
            color="primary"
            variant="outlined"
          />
        </Box>
        <TableContainer
          component={Paper}
          sx={{ borderRadius: "12px", overflow: "hidden", backgroundColor: theme.palette.background.paper }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: theme.palette.mode === 'light' ? "#f8f9ff" : "rgba(55, 65, 81, 0.5)" }}>
                <TableCell sx={{ fontWeight: "600", color: theme.palette.text.primary }}>S.No</TableCell>
                <TableCell sx={{ fontWeight: "600", color: theme.palette.text.primary }}>Course Name</TableCell>
                <TableCell sx={{ fontWeight: "600", color: theme.palette.text.primary }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedFolders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ color: theme.palette.text.secondary }}>
                    {folderSearch
                      ? "No Courses found matching your search"
                      : "No Courses available"}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedFolders.map((folder, index) => (
                  <TableRow key={folder.id} sx={{ "&:hover": { bgcolor: theme.palette.mode === 'light' ? "#f5f5f5" : "rgba(55, 65, 81, 0.3)" } }}>
                    <TableCell sx={{ color: theme.palette.text.primary }}>
                      {folderPage * folderRowsPerPage + index + 1}
                    </TableCell>
                    <TableCell sx={{ color: theme.palette.text.primary }}>{folder.name}</TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => handleOpenQuizModal(folder)}
                        size="small"

                        sx={{ mr: 1 }}
                      >
                        Convert to Quiz
                      </Button>
                      <Button
                        variant="contained"
                        color="info"
                        onClick={() => handleOpenViewQuizzesModal(folder)}
                        size="small"
                      >
                        View Quizzes
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredFolders.length}
            rowsPerPage={folderRowsPerPage}
            page={folderPage}
            onPageChange={handleFolderPageChange}
            onRowsPerPageChange={handleFolderRowsPerPageChange}
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.background.paper,
            }}
          />
        </TableContainer>
      </Paper>

      {/* Created Quizzes Section */}
      <Paper
        sx={{
          p: 4,
          mb: 4,
          borderRadius: "16px",
          boxShadow: "0 8px 30px rgba(0, 0, 0, 0.1)",
          border: `1px solid ${theme.palette.divider}`,
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
          },
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5" sx={{ color: theme.palette.text.primary, fontWeight: "700" }}>
            Created Quizzes
          </Typography>
          <Button
            variant="outlined"
            onClick={handleExportAllQuizzes}
            startIcon={
              <i className="bi bi-download" style={{ fontSize: "1.2rem" }}></i>
            }
            sx={{
              color: "#667eea",
              borderColor: "#667eea",
              fontWeight: "600",
              borderRadius: "12px",
              textTransform: "none",
              "&:hover": {
                background: theme.palette.mode === 'light' ? "rgba(102, 126, 234, 0.08)" : "rgba(132, 144, 255, 0.08)",
                borderColor: "#667eea",
              },
            }}
          >
            Export All
          </Button>
        </Box>
        <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
        {/* Quiz Search */}
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            placeholder="Search Quizzes..."
            value={quizSearch}
            onChange={(e) => {
              setQuizSearch(e.target.value);
              setAllQuizPage(0);
            }}
            variant="outlined"
            size="small"
            sx={{ width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: quizSearch && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={clearQuizSearch}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ minWidth: 200, ml: 2 }} size="small">
            <InputLabel sx={{ color: theme.palette.text.primary }}>Filter by Course</InputLabel>
            <Select
              value={selectedFolderFilter ? selectedFolderFilter.id : ""}
              onChange={(e) => {
                const folderId = e.target.value;
                setSelectedFolderFilter(
                  folderId ? folders.find((f) => f.id === folderId) : null
                );
                setAllQuizPage(0);
              }}
              label="Filter by course"
              sx={{ color: theme.palette.text.primary }}

              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300, // Set max height for scrolling
                    "&::-webkit-scrollbar": {
                      width: "10px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: theme.palette.mode === 'dark' ? '#2d3748' : '#f1f5f9',
                      borderRadius: "6px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: theme.palette.mode === 'dark' ? '#475569' : '#94a3b8',
                      borderRadius: "6px",
                      border: `2px solid ${theme.palette.mode === 'dark' ? '#2d3748' : '#f1f5f9'}`,
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: '#64748b',
                    },
                    // Firefox
                    scrollbarWidth: "thin",
                    scrollbarColor: theme.palette.mode === 'dark'
                      ? '#475569 #2d3748'
                      : '#94a3b8 #f1f5f9',
                  },
                },
              }}
            >
              <MenuItem value="">All Courses</MenuItem>
              {folders.map((folder) => (
                <MenuItem key={folder.id} value={folder.id}>
                  {folder.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Chip
            label={`${filteredQuizzes.length} Quiz${filteredQuizzes.length !== 1 ? "zes" : ""}`}
            color="primary"
            variant="outlined"
          />
        </Box>

        {loadingQuizzes ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={4}
          >
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ borderRadius: "12px", overflow: "hidden", backgroundColor: theme.palette.background.paper }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: theme.palette.mode === 'light' ? "#f8f9ff" : "rgba(55, 65, 81, 0.5)" }}>
                  <TableCell sx={{ fontWeight: "600", color: theme.palette.text.primary }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: "600", color: theme.palette.text.primary }}>Quiz Title</TableCell>
                  <TableCell sx={{ fontWeight: "600", color: theme.palette.text.primary }}>Course</TableCell>
                  <TableCell sx={{ fontWeight: "600", color: theme.palette.text.primary, textAlign: "center" }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedAllQuizzes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ color: theme.palette.text.secondary }}>
                      {quizSearch
                        ? "No quizzes found matching your search"
                        : "No quizzes created yet"}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedAllQuizzes.map((quiz, index) => (
                    <TableRow key={quiz.id} sx={{ "&:hover": { bgcolor: theme.palette.mode === 'light' ? "#f5f5f5" : "rgba(55, 65, 81, 0.3)" } }}>
                      <TableCell sx={{ color: theme.palette.text.primary }}>
                        {allQuizPage * allQuizRowsPerPage + index + 1}
                      </TableCell>
                      <TableCell sx={{ color: theme.palette.text.primary }}>{quiz.title}</TableCell>
                      <TableCell sx={{ color: theme.palette.text.primary }}>{quiz.folder_name}</TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        <Button
                          variant="outlined"
                          color="secondary"
                          size="small"
                          onClick={async () => {
                            setSelectedQuizDetails(quiz);
                            setResultModalOpen(true);
                            await fetchStudentsForResult();
                          }}
                          sx={{ mr: 1 }}
                        >
                          View Result
                        </Button>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={() => handleOpenDetails(quiz)}
                          sx={{ mr: 1 }}
                        >
                          View Details
                        </Button>
                        <Button
                          variant="outlined"
                          color="info"
                          size="small"
                          onClick={() => handleExportQuiz(quiz.id)}
                          startIcon={<i className="bi bi-download" style={{ fontSize: "1rem" }}></i>}
                        >
                          Export Q/A
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredQuizzes.length}
              rowsPerPage={allQuizRowsPerPage}
              page={allQuizPage}
              onPageChange={handleAllQuizPageChange}
              onRowsPerPageChange={handleAllQuizRowsPerPageChange}
              sx={{
                borderTop: `1px solid ${theme.palette.divider}`,
                bgcolor: theme.palette.background.paper,
              }}
            />
          </TableContainer>
        )}
      </Paper>

      {/* Modals (unchanged) */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        PaperProps={{
          style: {
            borderRadius: "20px",
            padding: "10px",
            minWidth: "400px",
            maxWidth: "50vw",
          },
        }}
      >
        <DialogTitle
          style={{
            fontSize: "1.8rem",
            fontWeight: "800",
            background: theme.palette.mode === 'light'
              ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            paddingBottom: "10px",
            borderBottom: `2px solid ${theme.palette.divider}`,
          }}
        >
          <i className="bi bi-folder-plus" style={{ marginRight: "10px" }}></i>
          Assign course to {selectedStudent?.name}
        </DialogTitle>
        <DialogContent style={{ paddingTop: "24px" }}>
          <DialogContentText
            style={{
              marginBottom: "24px",
              color: theme.palette.text.secondary,
              fontSize: "1.05rem",
              lineHeight: 1.6,
            }}
          >
            Select a course to assign to {selectedStudent?.name}.
          </DialogContentText>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel sx={{ fontWeight: "600", color: theme.palette.text.primary }}>
              Select course
            </InputLabel>
            <Select
              value={selectedFolder ? selectedFolder.name : ""}
              onChange={(e) =>
                setSelectedFolder(
                  folders.find((f) => f.name === e.target.value)
                )
              }
              label="Select Course"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  border: `2px solid ${theme.palette.divider}`,
                  background: theme.palette.background.paper,
                },
              }}
            >
              {folders.map((folder) => {
                const isAssigned = selectedStudent?.assigned_folders.some(
                  (assigned) => assigned.name === folder.name
                );
                return (
                  <MenuItem
                    key={folder.id}
                    value={folder.name}
                    disabled={isAssigned}
                    sx={{
                      "&.Mui-disabled": {
                        color: theme.palette.text.secondary,
                      },
                    }}
                  >
                    {folder.name} {isAssigned ? "(Already Assigned)" : ""}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseModal}
            sx={{
              color: theme.palette.text.secondary,
              padding: "10px 24px",
              fontSize: "1rem",
              fontWeight: "600",
              borderRadius: "10px",
              textTransform: "none",
              "&:hover": {
                background: theme.palette.mode === 'light' ? "#f3f4f6" : "rgba(55, 65, 81, 0.5)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            disabled={!selectedFolder}
            variant="contained"
            sx={{
              background: theme.palette.mode === 'light'
                ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
              color: "white",
              padding: "10px 28px",
              fontSize: "1rem",
              fontWeight: "700",
              borderRadius: "10px",
              textTransform: "none",
              boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
              "&:hover": {
                background: theme.palette.mode === 'light'
                  ? "linear-gradient(135deg, #764ba2 0%, #667eea 100%)"
                  : "linear-gradient(135deg, #9333ea 0%, #818cf8 100%)",
                boxShadow: "0 6px 20px rgba(102, 126, 234, 0.5)",
              },
              "&.Mui-disabled": {
                background: theme.palette.mode === 'light' ? "#d1d5db" : "rgba(209, 213, 219, 0.5)",
                color: "white",
                opacity: 0.6,
              },
            }}
          >
            <i
              className="bi bi-check-circle"
              style={{ marginRight: "8px" }}
            ></i>
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={removeModalOpen}
        onClose={handleCloseRemoveModal}
        PaperProps={{
          style: {
            borderRadius: "20px",
            padding: "10px",
            minWidth: "400px",
            maxWidth: "50vw",
          },
        }}
      >
        <DialogTitle
          style={{
            fontSize: "1.8rem",
            fontWeight: "800",
            background: theme.palette.mode === 'light'
              ? "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)"
              : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            paddingBottom: "10px",
            borderBottom: `2px solid ${theme.palette.divider}`,
          }}
        >
          <i className="bi bi-folder-minus" style={{ marginRight: "10px" }}></i>
          Remove course from {selectedStudent?.name}
        </DialogTitle>
        <DialogContent style={{ paddingTop: "24px" }}>
          <DialogContentText
            style={{
              marginBottom: "24px",
              color: theme.palette.text.secondary,
              fontSize: "1.05rem",
              lineHeight: 1.6,
            }}
          >
            Select a course to remove from {selectedStudent?.name}.
          </DialogContentText>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel sx={{ fontWeight: "600", color: theme.palette.text.primary }}>
              Select Course
            </InputLabel>
            <Select
              value={selectedFolder ? selectedFolder.name : ""}
              onChange={(e) => setSelectedFolder({ name: e.target.value })}
              label="Select Course"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  border: `2px solid ${theme.palette.divider}`,
                  background: theme.palette.background.paper,
                },
              }}
            >
              {selectedStudent?.assigned_folders.map((folder) => (
                <MenuItem key={folder.id} value={folder.name}>
                  {folder.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseRemoveModal}
            sx={{
              color: theme.palette.text.secondary,
              padding: "10px 24px",
              fontSize: "1rem",
              fontWeight: "600",
              borderRadius: "10px",
              textTransform: "none",
              "&:hover": {
                background: theme.palette.mode === 'light' ? "#f3f4f6" : "rgba(55, 65, 81, 0.5)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRemove}
            disabled={!selectedFolder}
            variant="contained"
            sx={{
              background: theme.palette.mode === 'light'
                ? "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)"
                : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
              color: "white",
              padding: "10px 28px",
              fontSize: "1rem",
              fontWeight: "700",
              borderRadius: "10px",
              textTransform: "none",
              boxShadow: "0 4px 15px rgba(244, 67, 54, 0.4)",
              "&:hover": {
                background: theme.palette.mode === 'light'
                  ? "linear-gradient(135deg, #d32f2f 0%, #f44336 100%)"
                  : "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)",
                boxShadow: "0 6px 20px rgba(244, 67, 54, 0.5)",
              },
              "&.Mui-disabled": {
                background: theme.palette.mode === 'light' ? "#d1d5db" : "rgba(209, 213, 219, 0.5)",
                color: "white",
                opacity: 0.6,
              },
            }}
          >
            <i className="bi bi-trash" style={{ marginRight: "8px" }}></i>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={quizModalOpen}
        onClose={handleCloseQuizModal}
        PaperProps={{
          style: {
            borderRadius: "20px",
            padding: "10px 0",
            minWidth: "600px",
            maxWidth: "90vw",
          },
        }}
      >
        <DialogTitle
          style={{
            fontSize: "1.8rem",
            fontWeight: "800",
            background: theme.palette.mode === 'light'
              ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            paddingBottom: "10px",
            borderBottom: `2px solid ${theme.palette.divider}`,
          }}
        >
          <i
            className="bi bi-file-earmark-plus"
            style={{ marginRight: "10px" }}
          ></i>
          Create Quiz for Course: {selectedFolder?.name}
        </DialogTitle>
        <DialogContent

          //  <DialogContent style={{ paddingTop: "24px" }}>
          sx={{
            // paddingTop: "24px",
            // maxHeight: "70vh",
            // overflowY: "auto",

            "&::-webkit-scrollbar": { width: "10px", height: "10px" },
            "&::-webkit-scrollbar-track": {
              background: theme.palette.mode === "dark" ? "#2d3748" : "#f1f5f9",
              borderRadius: "6px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: theme.palette.mode === "dark" ? "#475569" : "#94a3b8",
              borderRadius: "6px",
              border: `2px solid ${theme.palette.mode === "dark" ? "#2d3748" : "#f1f5f9"
                }`,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#64748b",
            },
            scrollbarWidth: "thin",
            scrollbarColor:
              theme.palette.mode === "dark"
                ? "#475569 #2d3748"
                : "#94a3b8 #f1f5f9",
          }}
        >

          <DialogContentText
            style={{
              marginBottom: "24px",
              color: theme.palette.text.secondary,
              fontSize: "1.05rem",
            }}
          >
            Configure your quiz settings below.
          </DialogContentText>
          <div style={{ marginBottom: "24px", position: "relative" }}>
            <label
              style={{
                display: "block",
                marginBottom: "10px",
                fontWeight: "700",
                fontSize: "1.1rem",
                color: theme.palette.text.primary,
              }}
            >
              Quiz Title <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <TextField
              fullWidth
              margin="normal"
              value={quizTitle}
              onChange={(e) => setQuizTitle(e.target.value)}
              required
              variant="outlined"
              style={{
                borderRadius: "12px",
                border: `2px solid ${theme.palette.divider}`,
              }}
            />
          </div>
          <FormControl component="fieldset" style={{ marginBottom: "24px" }}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={negMarking}
                    onChange={(e) => setNegMarking(e.target.checked)}
                    style={{ color: "#667eea" }}
                  />
                }
                label={
                  <span style={{ fontSize: "1rem", color: theme.palette.text.primary }}>
                    Enable Negative Marking
                  </span>
                }
              />
            </FormGroup>
          </FormControl>
          <div style={{ marginBottom: "24px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "10px",
                fontWeight: "700",
                fontSize: "1.1rem",
                color: theme.palette.text.primary,
              }}
            >
              Question Types <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <FormControl component="fieldset">
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={questionTypes.length === allTypes.length}
                      onChange={handleAllTypesChange}
                      style={{ color: "#667eea" }}
                    />
                  }
                  label={
                    <span style={{ fontSize: "1rem", color: theme.palette.text.primary }}>
                      Select All
                    </span>
                  }
                />
                {allTypes.map((type) => (
                  <FormControlLabel
                    key={type}
                    control={
                      <Checkbox
                        checked={questionTypes.includes(type)}
                        onChange={handleTypeChange(type)}
                        style={{ color: "#667eea" }}
                      />
                    }
                    label={
                      <span style={{ fontSize: "1rem", color: theme.palette.text.primary }}>
                        {getTypeLabel(type)}
                      </span>
                    }
                  />
                ))}
              </FormGroup>
            </FormControl>
          </div>
          <div style={{ marginBottom: "24px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "10px",
                fontWeight: "700",
                fontSize: "1.1rem",
                color: theme.palette.text.primary,
              }}
            >
              Passing Percentage (%)
            </label>
            <TextField
              fullWidth
              type="number"
              value={passingPercentage}
              onChange={(e) => setPassingPercentage(parseFloat(e.target.value))}
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              variant="outlined"
              style={{
                borderRadius: "12px",
                border: `2px solid ${theme.palette.divider}`,
              }}
            />
          </div>
          <div style={{ marginBottom: "24px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "10px",
                fontWeight: "700",
                fontSize: "1.1rem",
                color: theme.palette.text.primary,
              }}
            >
              Max Attempts
            </label>
            <TextField
              fullWidth
              type="number"
              value={maxAttempts}
              onChange={(e) => setMaxAttempts(parseInt(e.target.value))}
              inputProps={{ min: 1 }}
              variant="outlined"
              style={{
                borderRadius: "12px",
                border: `2px solid ${theme.palette.divider}`,
              }}
            />
          </div>
          <div style={{ marginBottom: "24px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "10px",
                fontWeight: "700",
                fontSize: "1.1rem",
                color: theme.palette.text.primary,
              }}
            >
              Time Limit (minutes)
            </label>
            <TextField
              fullWidth
              type="number"
              value={timeLimit}
              onChange={(e) => setTimeLimit(e.target.value)}
              placeholder="Leave blank for no limit"
              inputProps={{ min: 1 }}
              variant="outlined"
              style={{
                borderRadius: "12px",
                border: `2px solid ${theme.palette.divider}`,
              }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseQuizModal}
            sx={{
              color: theme.palette.text.secondary,
              padding: "10px 24px",
              fontSize: "1rem",
              fontWeight: "600",
              borderRadius: "10px",
              textTransform: "none",
              "&:hover": {
                background: theme.palette.mode === 'light' ? "#f3f4f6" : "rgba(55, 65, 81, 0.5)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateQuiz}
            disabled={!quizTitle || questionTypes.length === 0}
            variant="contained"
            sx={{
              background: theme.palette.mode === 'light'
                ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
              color: "white",
              padding: "10px 28px",
              fontSize: "1rem",
              fontWeight: "700",
              borderRadius: "10px",
              textTransform: "none",
              boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
              "&:hover": {
                background: theme.palette.mode === 'light'
                  ? "linear-gradient(135deg, #764ba2 0%, #667eea 100%)"
                  : "linear-gradient(135deg, #9333ea 0%, #818cf8 100%)",
                boxShadow: "0 6px 20px rgba(102, 126, 234, 0.5)",
              },
              "&.Mui-disabled": {
                background: theme.palette.mode === 'light' ? "#d1d5db" : "rgba(209, 213, 219, 0.5)",
                color: "white",
                opacity: 0.6,
              },
            }}
          >
            <i
              className="bi bi-check-circle"
              style={{ marginRight: "8px" }}
            ></i>
            Create Quiz
          </Button>
        </DialogActions>
      </Dialog>

      <Modal open={viewQuizzesModalOpen} onClose={handleCloseViewQuizzesModal}>
        <Box
          sx={{
            p: 0,
            bgcolor: theme.palette.background.paper,
            mx: "auto",
            mt: "3%",
            mb: "3%",
            width: "90%",
            maxWidth: 900,
            maxHeight: "90vh",
            borderRadius: "20px",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              p: 3,
              background: theme.palette.mode === 'light'
                ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
              color: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              Quizzes for Course: {selectedFolder?.name}
            </Typography>
            <Box>
              <Button
                variant="contained"
                onClick={handleExportFolderQuizzes}
                startIcon={
                  <i className="bi bi-download" style={{ fontSize: "1.2rem" }}></i>
                }
                sx={{
                  background: "white",
                  color: "black",
                  fontWeight: 700,
                  borderRadius: "12px",
                  px: 2,
                  py: 1,
                  textTransform: "none",
                  boxShadow: "0 4px 15px rgba(139, 92, 246, 0.3)",
                  mr: 1,
                }}
              >
                Export All
              </Button>
            </Box>
          </Box>
          {/* Modal Body */}
          {/* <Box sx={{ p: 3, overflowY: "auto", flex: 1 }}> */}
          <Box
            sx={{
              p: 3,
              overflowY: "auto",
              flex: 1,

              "&::-webkit-scrollbar": { width: "10px", height: "10px" },
              "&::-webkit-scrollbar-track": {
                background: theme.palette.mode === "dark" ? "#2d3748" : "#f1f5f9",
                borderRadius: "6px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: theme.palette.mode === "dark" ? "#475569" : "#94a3b8",
                borderRadius: "6px",
                border: `2px solid ${theme.palette.mode === "dark" ? "#2d3748" : "#f1f5f9"
                  }`,
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#64748b",
              },
              scrollbarWidth: "thin",
              scrollbarColor:
                theme.palette.mode === "dark"
                  ? "#475569 #2d3748"
                  : "#94a3b8 #f1f5f9",
            }}
          >

            {folderQuizzes.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 5 }}>
                <Typography variant="h6" color={theme.palette.text.secondary}>
                  No quizzes found for this Course.
                </Typography>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: "12px",
                  boxShadow: "none",
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: theme.palette.mode === 'light' ? "#f8f9ff" : "rgba(55, 65, 81, 0.5)" }}>
                      <TableCell sx={{ fontWeight: "600", color: theme.palette.text.primary }}>S.No</TableCell>
                      <TableCell sx={{ fontWeight: "600", color: theme.palette.text.primary }}>
                        Quiz Title
                      </TableCell>
                      <TableCell sx={{ fontWeight: "600", color: theme.palette.text.primary }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedQuizzes.map((quiz, index) => (
                      <TableRow
                        key={quiz.id}
                        sx={{ "&:hover": { bgcolor: theme.palette.mode === 'light' ? "#f5f5f5" : "rgba(55, 65, 81, 0.3)" } }}
                      >
                        <TableCell sx={{ color: theme.palette.text.primary }}>
                          {quizPage * quizRowsPerPage + index + 1}
                        </TableCell>
                        <TableCell sx={{ color: theme.palette.text.primary }}>{quiz.title}</TableCell>
                        <TableCell>
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            onClick={() => {
                              setSelectedQuiz(quiz);
                              setDeleteQuizModalOpen(true);
                            }}
                            sx={{
                              textTransform: "none",
                              borderRadius: "8px",
                              padding: "6px 16px",
                            }}
                          >
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={folderQuizzes.length}
                  rowsPerPage={quizRowsPerPage}
                  page={quizPage}
                  onPageChange={handleQuizPageChange}
                  onRowsPerPageChange={handleQuizRowsPerPageChange}
                  sx={{ border: "none" }}
                />
              </TableContainer>
            )}
          </Box>
          {/* Modal Footer */}
          <Box
            sx={{
              p: 2,
              bgcolor: theme.palette.mode === 'light' ? "#f5f5f5" : "rgba(55, 65, 81, 0.5)",
              borderTop: `1px solid ${theme.palette.divider}`,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="contained"
              onClick={handleCloseViewQuizzesModal}
              sx={{
                minWidth: 120,
                borderRadius: "12px",
                padding: "10px 24px",
                fontWeight: "600",
                textTransform: "none",
                background: theme.palette.mode === 'light'
                  ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
                "&:hover": {
                  background: theme.palette.mode === 'light'
                    ? "linear-gradient(135deg, #764ba2 0%, #667eea 100%)"
                    : "linear-gradient(135deg, #9333ea 0%, #818cf8 100%)",
                },
              }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      <Dialog
        open={deleteQuizModalOpen}
        onClose={() => {
          setDeleteQuizModalOpen(false);
          setSelectedQuiz(null);
        }}
        maxWidth="sm"
        fullWidth
        sx={{ "& .MuiPaper-root": { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ color: theme.palette.text.primary }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: theme.palette.text.secondary }}>
            Are you sure you want to delete the quiz:{" "}
            <strong>{selectedQuiz?.title}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteQuizModalOpen(false);
              setSelectedQuiz(null);
            }}
            color="secondary"
          >
            Cancel
          </Button>
          <Button onClick={handleDeleteQuiz} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quiz Details Modal */}
      <Dialog
        open={viewDetailsModalOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "24px",
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            background: theme.palette.mode === 'light'
              ? "linear-gradient(145deg, #f8f9ff 0%, #ffffff 100%)"
              : "linear-gradient(145deg, #1e293b 0%, #0f172a 100%)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: theme.palette.mode === 'light'
              ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
            color: "white",
            py: 4,
            textAlign: "center",
            fontWeight: 800,
            fontSize: "2rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            mb: 2
          }}
        >
          Quiz Details
        </DialogTitle>
        {/* <DialogContent sx={{ p: 4, bgcolor: theme.palette.mode === 'light' ? "#f8f9ff" : "rgba(55, 65, 81, 0.5)" }}> */}
        <DialogContent
          sx={{
            // p: 4,
            // bgcolor:
            //   theme.palette.mode === "light"
            //     ? "#f8f9ff"
            //     : "rgba(55, 65, 81, 0.5)",
            // maxHeight: "70vh",
            overflowY: "auto",

            "&::-webkit-scrollbar": { width: "10px", height: "10px" },
            "&::-webkit-scrollbar-track": {
              background: theme.palette.mode === "dark" ? "#2d3748" : "#f1f5f9",
              borderRadius: "6px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: theme.palette.mode === "dark" ? "#475569" : "#94a3b8",
              borderRadius: "6px",
              border: `2px solid ${theme.palette.mode === "dark" ? "#2d3748" : "#f1f5f9"
                }`,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#64748b",
            },
            scrollbarWidth: "thin",
            scrollbarColor:
              theme.palette.mode === "dark"
                ? "#475569 #2d3748"
                : "#94a3b8 #f1f5f9",
          }}
        >

          {selectedQuizDetails && (
            <Box>
              {/* Title Section */}
              <Box
                sx={{
                  bgcolor: theme.palette.background.paper,
                  borderRadius: "16px",
                  p: 3,
                  mb: 3,
                  boxShadow: "0 4px 15px rgba(0, 0, 0, 0.08)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                  <i className="bi bi-card-heading" style={{ fontSize: "1.8rem", color: "#8b5cf6" }}></i>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: "#8b5cf6" }}>
                    {selectedQuizDetails.title}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Chip
                    icon={<i className="bi bi-folder-fill" style={{ fontSize: "1rem", marginLeft: "8px" }}></i>}
                    label={selectedQuizDetails.folder_name}
                    sx={{
                      bgcolor: theme.palette.mode === 'light' ? "#fef3c7" : "rgba(245, 158, 11, 0.2)",
                      color: "#f59e0b",
                      fontWeight: 600,
                      fontSize: "0.95rem",
                    }}
                  />
                </Box>
              </Box>
              {/* Stats Grid */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                  gap: 2,
                  mb: 0,
                }}
              >
                {/* Total Questions Card */}
                <Box
                  sx={{
                    bgcolor: theme.palette.background.paper,
                    borderRadius: "16px",
                    p: 3,
                    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.08)",
                    transition: "transform 0.2s",
                    "&:hover": { transform: "translateY(-4px)" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        bgcolor: "#dbeafe",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <i className="bi bi-list-ol" style={{ fontSize: "1.3rem", color: "#3b82f6" }}></i>
                    </Box>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 600, textTransform: "uppercase", fontSize: "0.75rem" }}>
                      Total Questions
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.text.primary }}>
                    {selectedQuizDetails.total_questions}
                  </Typography>
                </Box>

                {/* Passing Score Card */}
                <Box
                  sx={{
                    bgcolor: theme.palette.background.paper,
                    borderRadius: "16px",
                    p: 3,
                    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.08)",
                    transition: "transform 0.2s",
                    "&:hover": { transform: "translateY(-4px)" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        bgcolor: "#dbeafe",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <i className="bi bi-check-circle-fill" style={{ fontSize: "1.3rem", color: "#3b82f6" }}></i>
                    </Box>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 600, textTransform: "uppercase", fontSize: "0.75rem" }}>
                      Passing Score
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.text.primary }}>
                    {selectedQuizDetails.passing_percentage}%
                  </Typography>
                </Box>

                {/* Max Attempts Card */}
                <Box
                  sx={{
                    bgcolor: theme.palette.background.paper,
                    borderRadius: "16px",
                    p: 3,
                    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.08)",
                    transition: "transform 0.2s",
                    "&:hover": { transform: "translateY(-4px)" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        bgcolor: "#ede9fe",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <i className="bi bi-arrow-repeat" style={{ fontSize: "1.3rem", color: "#8b5cf6" }}></i>
                    </Box>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 600, textTransform: "uppercase", fontSize: "0.75rem" }}>
                      Max Attempts
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.text.primary }}>
                    {selectedQuizDetails.max_attempts}
                  </Typography>
                </Box>


                {/* Time Limit Card */}
                <Box
                  sx={{
                    bgcolor: theme.palette.background.paper,
                    borderRadius: "16px",
                    p: 3,
                    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.08)",
                    transition: "transform 0.2s",
                    "&:hover": { transform: "translateY(-4px)" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        bgcolor: "#fee2e2",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <i className="bi bi-clock-fill" style={{ fontSize: "1.3rem", color: "#ef4444" }}></i>
                    </Box>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 600, textTransform: "uppercase", fontSize: "0.75rem" }}>
                      Time Limit
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.text.primary }}>
                    {selectedQuizDetails.time_limit_minutes ? `${selectedQuizDetails.time_limit_minutes} min` : "No limit"}
                  </Typography>
                </Box>

                {/* Question Types Section (spanning 3 columns) */}
                <Box
                  sx={{
                    gridColumn: { xs: "1 / -1", sm: "span 2" },
                    bgcolor: theme.palette.background.paper,
                    borderRadius: "16px",
                    transition: "transform 0.2s",
                    "&:hover": { transform: "translateY(-4px)" },
                    p: 3,
                    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.08)",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.text.primary,
                      mb: 1.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <i className="bi bi-patch-question-fill" style={{ color: "#06b6d4", fontSize: "1.3rem" }}></i>
                    Question Types
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {selectedQuizDetails.question_types.map((type) => (
                      <Chip
                        key={type}
                        label={getTypeLabel(type)}
                        sx={{
                          background: theme.palette.mode === 'light'
                            ? "linear-gradient(135deg, #a5b4fc 0%, #c4b5fd 100%)"
                            : "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                          color: "#4c1d95",
                          fontWeight: 600,
                          borderRadius: "12px",
                          boxShadow: "0 2px 8px rgba(165, 180, 252, 0.3)",
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            background: theme.palette.mode === 'light' ? "#f8f9ff" : "rgba(55, 65, 81, 0.5)",
            justifyContent: "center",
          }}
        >
          <Button
            onClick={handleCloseDetails}
            variant="contained"
            sx={{
              minWidth: 180,
              py: 1.5,
              fontWeight: 700,
              borderRadius: "16px",
              background: theme.palette.mode === 'light'
                ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
              boxShadow: "0 8px 25px rgba(102, 126, 234, 0.4)",
              textTransform: "none",
              fontSize: "1.1rem",
              "&:hover": {
                background: theme.palette.mode === 'light'
                  ? "linear-gradient(135deg, #764ba2 0%, #667eea 100%)"
                  : "linear-gradient(135deg, #9333ea 0%, #818cf8 100%)",
                boxShadow: "0 10px 30px rgba(102, 126, 234, 0.5)",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={resultModalOpen}
        onClose={() => {
          setResultModalOpen(false);
          setSelectedStudentForResult(null);
          setQuizResults(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "24px",
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            background: theme.palette.mode === 'light'
              ? "linear-gradient(145deg, #f8f9ff 0%, #ffffff 100%)"
              : "linear-gradient(145deg, #1e293b 0%, #0f172a 100%)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: theme.palette.mode === 'light'
              ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
            color: "white",
            py: 4,
            textAlign: "center",
            fontWeight: 800,
            fontSize: "2rem",
            mb: 4
          }}
        >
          Quiz Results for: {selectedQuizDetails?.title}
        </DialogTitle>
        {/* <DialogContent sx={{ p: 4, bgcolor: theme.palette.mode === 'light' ? "#f8f9ff" : "rgba(55, 65, 81, 0.5)" }}> */}
        <DialogContent
          sx={{
            p: 4,
            bgcolor:
              theme.palette.mode === "light"
                ? "#f8f9ff"
                : "rgba(55, 65, 81, 0.5)",
            maxHeight: "70vh",
            overflowY: "auto",

            // SCROLLBAR STYLING
            "&::-webkit-scrollbar": {
              width: "10px",
              height: "10px",
            },
            "&::-webkit-scrollbar-track": {
              background: theme.palette.mode === "dark" ? "#2d3748" : "#f1f5f9",
              borderRadius: "6px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: theme.palette.mode === "dark" ? "#475569" : "#94a3b8",
              borderRadius: "6px",
              border: `2px solid ${theme.palette.mode === "dark" ? "#2d3748" : "#f1f5f9"
                }`,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#64748b",
            },

            // Firefox
            scrollbarWidth: "thin",
            scrollbarColor:
              theme.palette.mode === "dark"
                ? "#475569 #2d3748"
                : "#94a3b8 #f1f5f9",
          }}
        >

          <FormControl fullWidth sx={{ mb: 2, mt: 2 }}>
            <InputLabel sx={{ color: theme.palette.text.primary }}>Student</InputLabel>
            <Select
              value={selectedStudentForResult?.id || ""}
              onChange={(e) => {
                const student = studentsForResult.find((s) => s.id === e.target.value);
                setSelectedStudentForResult(student);
                if (student && selectedQuizDetails) {
                  fetchQuizResults(student.id, selectedQuizDetails.id);
                }
              }}
              label="Student"
              sx={{ color: theme.palette.text.primary }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300, // Set max height for scrolling
                    "&::-webkit-scrollbar": {
                      width: "10px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: theme.palette.mode === 'dark' ? '#2d3748' : '#f1f5f9',
                      borderRadius: "6px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: theme.palette.mode === 'dark' ? '#475569' : '#94a3b8',
                      borderRadius: "6px",
                      border: `2px solid ${theme.palette.mode === 'dark' ? '#2d3748' : '#f1f5f9'}`,
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: '#64748b',
                    },
                    // Firefox
                    scrollbarWidth: "thin",
                    scrollbarColor: theme.palette.mode === 'dark'
                      ? '#475569 #2d3748'
                      : '#94a3b8 #f1f5f9',
                  },
                },
              }}
            >
              {studentsForResult.map((student) => (
                <MenuItem key={student.id} value={student.id}>
                  {student.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedStudentForResult && quizResults ? (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
                Results for {selectedStudentForResult.name}
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                  <strong>Quiz Name:</strong> {quizResults.quiz_name}
                </Typography>
                <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                  <strong>Total Attempts:</strong> {quizResults.total_attempts}
                </Typography>
                <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                  <strong>Passing Percentage:</strong> {quizResults.passing_percentage}%
                </Typography>
                <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                  <strong>Total Questions:</strong> {quizResults.total_questions}
                </Typography>
              </Box>
              <Box>
                {quizResults.attempts && quizResults.attempts.length > 0 ? (
                  quizResults.attempts.map((attempt, index) => (
                    <Paper
                      key={index}
                      sx={{
                        p: 2,
                        mb: 2,
                        borderLeft: `4px solid ${attempt.result === "Pass" ? "#4caf50" : "#f44336"
                          }`,
                        bgcolor: theme.palette.background.paper,
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ color: theme.palette.text.primary }}>
                        Attempt #{attempt.attempt_number}
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                        <strong>Score:</strong> {attempt.final_score}
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                        <strong>Result:</strong> {attempt.result}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={async () => {
                          await handleExportQuizReview(selectedStudentForResult.id, quizResults.quiz_id, attempt.quiz_attempt_id);
                        }}
                        startIcon={<i className="bi bi-download" style={{ fontSize: "1rem" }}></i>}
                        sx={{
                          mt: 2,
                          borderRadius: "8px",
                          textTransform: "none",
                          fontWeight: "600",
                          borderColor: "#667eea",
                          color: "#667eea",
                          "&:hover": {
                            borderColor: "#667eea",
                            background: "rgba(102, 126, 234, 0.08)",
                          },
                        }}
                      >
                        Export
                      </Button>
                    </Paper>
                  ))
                ) : (
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>No attempts recorded.</Typography>
                )}
              </Box>
            </Box>
          ) : selectedStudentForResult ? (
            <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>Loading results...</Typography>
          ) : (
            <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>Select a student to view results.</Typography>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            background: theme.palette.mode === 'light' ? "#f8f9ff" : "rgba(55, 65, 81, 0.5)",
            justifyContent: "center",
          }}
        >
          <Button
            onClick={() => {
              setResultModalOpen(false);
              setSelectedStudentForResult(null);
              setQuizResults(null);
            }}
            variant="contained"
            sx={{
              minWidth: 180,
              py: 1.5,
              fontWeight: 700,
              borderRadius: "16px",
              background: theme.palette.mode === 'light'
                ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
              boxShadow: "0 8px 25px rgba(102, 126, 234, 0.4)",
              textTransform: "none",
              fontSize: "1.1rem",
              "&:hover": {
                background: theme.palette.mode === 'light'
                  ? "linear-gradient(135deg, #764ba2 0%, #667eea 100%)"
                  : "linear-gradient(135deg, #9333ea 0%, #818cf8 100%)",
                boxShadow: "0 10px 30px rgba(102, 126, 234, 0.5)",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Modal open={infoModalOpen} onClose={() => setInfoModalOpen(false)}>
        <Box
          sx={{
            p: 0,
            bgcolor: theme.palette.background.paper,
            mx: "auto",
            mt: "3%",
            mb: "3%",
            width: "90%",
            maxWidth: 800,
            maxHeight: "90vh",
            borderRadius: "16px",
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            "&::-webkit-scrollbar": {
              width: "10px",
              height: "10px",
            },
            "&::-webkit-scrollbar-track": {
              background: theme.palette.mode === 'dark' ? '#2d3748' : '#f1f5f9',
              borderRadius: "6px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: theme.palette.mode === 'dark' ? '#475569' : '#94a3b8',
              borderRadius: "6px",
              border: `2px solid ${theme.palette.mode === 'dark' ? '#2d3748' : '#f1f5f9'}`,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: theme.palette.mode === 'dark' ? '#64748b' : '#64748b',
            },
            // Custom scrollbar for Firefox
            scrollbarWidth: "thin",
            scrollbarColor: theme.palette.mode === 'dark' ? '#475569 #2d3748' : '#94a3b8 #f1f5f9',
          }}

        >
          {/* Header */}
          <Box
            sx={{
              p: 3.5,
              background: theme.palette.mode === 'light'
                ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
              color: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: "700", mb: 0.5 }}>
                User Guide
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Quiz Creation
              </Typography>
            </Box>
          </Box>
          {/* Content */}
          <Box sx={{ p: 4, overflowY: "auto", flex: 1 }}>
            {/* Folders Section */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    background: theme.palette.mode === 'light'
                      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                      : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 2,
                  }}
                >
                  <FolderIcon sx={{ color: "white", fontSize: 20 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: "600", color: theme.palette.text.primary }}>
                  Courses
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7, pl: 7 }}>
                Use this section to view and select courses to convert into quizzes. Click "Convert to Quiz" to create a new quiz from a course.
              </Typography>
            </Box>
            {/* Quiz Creation Section */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    background: theme.palette.mode === 'light'
                      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                      : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 2,
                  }}
                >
                  <AssignmentIcon sx={{ color: "white", fontSize: 20 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: "600", color: theme.palette.text.primary }}>
                  Create Quiz
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7, pl: 7 }}>
                Fill in the quiz details (title, question types, passing percentage, etc.) and click "Create Quiz" to generate a new quiz.
              </Typography>
            </Box>
            {/* Created Quizzes Section */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    background: theme.palette.mode === 'light'
                      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                      : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 2,
                  }}
                >
                  <PlayArrowIcon sx={{ color: "white", fontSize: 20 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: "600", color: theme.palette.text.primary }}>
                  Created Quizzes
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7, pl: 7 }}>
                View all created quizzes. Use the search and filter options to find specific quizzes. Click "View Details" to see quiz details or "View Result" to check result of a particular student for that particular quiz.
              </Typography>
            </Box>
            {/* Export Section */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    background: theme.palette.mode === 'light'
                      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                      : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 2,
                  }}
                >
                  <i className="bi bi-download" style={{ color: "white", fontSize: 20 }}></i>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: "600", color: theme.palette.text.primary }}>
                  Export Quizzes
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7, pl: 7 }}>
                Export all quizzes or quizzes from a specific course as an Excel file for offline use.
              </Typography>
            </Box>
            {/* Pro Tips */}
            <Box
              sx={{
                p: 3,
                borderRadius: "12px",
                bgcolor: theme.palette.mode === 'light' ? "#f7fafc" : "rgba(55, 65, 81, 0.5)",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: "600", color: theme.palette.text.primary, mb: 2 }}>
                💡 Quick Tips
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                  • Use the search bar to quickly find courses or quizzes.
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                  • Enable negative marking if you want to penalize wrong answers.
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                  • Set a time limit if you want to restrict quiz duration.
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                  • Set the maximum number of attempts allowed for a quiz. Leave blank for unlimited attempts.
                </Typography>
              </Box>
            </Box>
          </Box>
          {/* Footer */}
          <Box
            sx={{
              p: 3,
              bgcolor: theme.palette.mode === 'light' ? "#f7fafc" : "rgba(55, 65, 81, 0.5)",
              borderTop: `1px solid ${theme.palette.divider}`,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="contained"
              onClick={() => setInfoModalOpen(false)}
              sx={{
                minWidth: 120,
                borderRadius: "10px",
                padding: "10px 28px",
                fontWeight: "600",
                textTransform: "none",
                background: theme.palette.mode === 'light'
                  ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
                boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                "&:hover": {
                  background: theme.palette.mode === 'light'
                    ? "linear-gradient(135deg, #764ba2 0%, #667eea 100%)"
                    : "linear-gradient(135deg, #9333ea 0%, #818cf8 100%)",
                  boxShadow: "0 6px 16px rgba(102, 126, 234, 0.4)",
                },
              }}
            >
              Got it
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => {
          setSnackbarOpen(false);
        }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => {
            setSnackbarOpen(false);
          }}
          severity={snackbarSeverity}
          sx={{
            width: "100%",
            fontSize: "1rem",
            fontWeight: "600",
            borderRadius: "12px",
            boxShadow: "0 8px 25px rgba(0, 0, 0, 0.15)",
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QuizCreationPage;
